import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useToast } from '@razorpay/blade/components';
import api from 'api';
import { AppError } from 'utils/AppError';
import { queryKeys } from '../../constants';
import { PayrollGetEmployeeVpfResponse } from 'api/gql-utils/vpf/types';
import { VpfData } from '../../types';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';

type UseEmployeeVpfDataProps = {
  peopleId: string;
};

const useEmployeeVpfData = ({ peopleId }: UseEmployeeVpfDataProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isM3S1Enabled } = useIsFeatureDisabledForRearch();

  // VPF Data Query
  const {
    data: vpfData,
    isLoading: isVpfLoading,
    error: vpfError,
    refetch: vpfRefetch,
  } = useQuery<PayrollGetEmployeeVpfResponse, AppError, VpfData | null>({
    queryKey: [queryKeys.FETCH_VPF_DETAILS, peopleId],
    queryFn: () => api.vpfQueries.getEmployeeVpfData(peopleId),
    select: (data: PayrollGetEmployeeVpfResponse) => {
      return data.payrollGetEmployeeVpf.data.peopleId
        ? {
            vpfAmount: data.payrollGetEmployeeVpf.data.vpfAmount,
            maximumAllowedAmount: data.payrollGetEmployeeVpf.data.maximumAllowedAmount,
          }
        : null;
    },
    enabled: isM3S1Enabled,
    onError: (error: AppError) => {},
  });

  const vpfOptIn = useMutation({
    mutationFn: api.vpfMutations.updateEmployeeVpf,
    onSuccess: () => {
      toast.show({
        color: 'positive',
        content: 'VPF amount updated successfully',
        autoDismiss: true,
        duration: 5000,
      });
      queryClient.invalidateQueries([queryKeys.FETCH_VPF_DETAILS, peopleId]);
    },
    onError: (error: AppError) => {
      toast.show({
        color: 'negative',
        content: error.message || 'Failed to update VPF. Please try again.',
        autoDismiss: true,
        duration: 5000,
      });
    },
  });

  const vpfOptOut = useMutation({
    mutationFn: api.vpfMutations.optOutEmployeeVpf,
    onSuccess: () => {
      toast.show({
        color: 'information',
        content: 'VPF opted out successfully',
        autoDismiss: true,
        duration: 5000,
      });
      queryClient.invalidateQueries([queryKeys.FETCH_VPF_DETAILS, peopleId]);
    },
    onError: (error: AppError) => {
      toast.show({
        color: 'negative',
        content: error.message || 'Failed to opt out of VPF. Please try again.',
        autoDismiss: true,
        duration: 5000,
      });
    },
  });

  const onVPFOptIn = (amount: string) => {
    vpfOptIn.mutate({
      input: {
        peopleId,
        vpfAmount: Number(amount),
      },
    });
  };

  const onVPFOptOut = () => {
    vpfOptOut.mutate({
      input: {
        peopleId,
      },
    });
  };

  return {
    // VPF Data
    vpfData,
    isVpfLoading,
    vpfError,
    vpfRefetch,
    // VPF Actions
    onVPFOptIn,
    onVPFOptOut,
  };
};

export default useEmployeeVpfData;
